import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { TablePagination, CircularProgress, IconButton, Chip, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { ProdButton } from '../../resources/styling';
import {
  AppContainer,
  MainContentContainer,
  BodyContainer,
} from '../../resources/styling/appStyle';
import { sa, DataTable, PillButton, SectionHeaderRow } from '../../components/superAdminUi';
import {
  impersonate,
  ScopeType,
  getCallbackUrlForProduct,
  getHomeCallbackUrl,
} from '../../utils/impersonateUtils';
import { Product, PRODUCT_DISPLAY_NAMES, EI_TIER_PRODUCT_IDS } from '../../utils/Constants';
import groupService from '../../services/api/matGroupService';
import { useCanArchive } from '../../hooks/useCanArchive';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { archiveMatAction, archiveSchoolAction } from './archive/archiveActions';
import { useArchiveConfirmation } from './archive/useArchiveConfirmation';
import { activeSchoolsOf, activeSchoolCountOf } from './activeSchools';

// Presentation-only redesign of the Super Admin Groups (MAT) listing. The tables, filters and
// pill actions are rebuilt on the shared Super Admin design system (purple-header DataTable,
// Cards, PillButtons) so the page matches the redesigned account screens. Every group/school
// service call, search, sort, tab filter, pagination and impersonation handler is preserved.

// Live / Test filter as the Figma pill tab bar (white bordered track, teal active pill).
const TabBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;  
`;

const TabPill = styled.button`
  border: none;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 14px;
  line-height: normal;
  padding: 12px 18px;
  border-radius: ${sa.radius.sm};
  background: ${(p) => (p.$active ? sa.color.teal : sa.color.tabInactive)};
  color: ${(p) => (p.$active ? sa.color.white : sa.color.text)};
  transition: background-color 0.15s ease-in-out;
`;

// Header search box, matching the redesigned account screens: bordered, leading icon.
const SearchBox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    font-size: 18px;
    color: ${sa.color.textGray};
    pointer-events: none;
  }
  input {
    width: 260px;
    max-width: 100%;
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    background: ${sa.color.white};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 9px 12px 9px 38px;

    &::placeholder {
      color: ${sa.color.textGray};
    }
    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

// A purple-header sort control: label plus a direction glyph reflecting the active column.
const SortHead = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${sa.color.white};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.medium};
  font-size: inherit;

  svg {
    font-size: 16px;
    opacity: ${(p) => (p.$active ? 1 : 0.55)};
  }
`;

const CellText = styled.span`
  color: ${sa.color.text};
`;

const ExpandToggle = styled(IconButton)`
  && {    
    color: ${sa.color.textGray};
  }
`;

const HomeButton = styled(PillButton)`
  background: #de9868;
`;

const SchoolPanel = styled.div`
  border-left: 3px solid ${sa.color.purple};
  background: #fafbfc;
  padding: 18px 24px;
`;

const SchoolPanelTitle = styled.h4`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
  margin: 0 0 14px;
`;

const CenterState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(p) => p.$pad || '48px 0'};
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${(p) => (p.$error ? sa.color.pink : sa.color.textGray)};
`;

const PaginationWrap = styled.div`
  .MuiTablePagination-root {
    font-family: ${sa.font.family};
    color: ${sa.color.textGray};
  }
`;

// The listing only ever shows a MAT's active schools, with their products under one key. A MAT
// whose schools collection is absent is kept as-is so its school count stays "unknown".
const withActiveSchools = (mat) => {
  if (activeSchoolCountOf(mat) === null) return mat;
  return {
    ...mat,
    Schools: activeSchoolsOf(mat).map((school) => ({
      ...school,
      SchoolProducts: school.SchoolProducts || school.schoolProductsList || [],
    })),
  };
};

const schoolIdsOf = (mat) =>
  (mat?.Schools || []).map((school) => school?.Id).filter((id) => !!id && id > 0);

function GroupListing() {
  const navigate = useHistory();
  const canArchive = useCanArchive();
  const { requestConfirmation, dialogProps: archiveDialogProps } = useArchiveConfirmation();

  const [value, setValue] = useState(0);
  const handleTabChange = (newValue) => {
    setValue(newValue);
  };

  const [adminDashboardInfo, setAdminDashboardInfo] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('asc'); // 'asc' or 'desc'
  const [orderBy, setOrderBy] = useState('Name'); // Default column to sort by
  const [expandedRows, setExpandedRows] = useState([]); // Track expanded rows
  const [searchQuery, setSearchQuery] = useState(''); // Search query state
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Debounced search query
  const [impersonateLoading, setImpersonateLoading] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loadedGroupDetails, setLoadedGroupDetails] = useState({}); // Cache for loaded group details
  const [loadingGroupDetails, setLoadingGroupDetails] = useState({}); // Track which groups are loading
  const groupDetailsInFlight = useRef({});
  const [countingSchoolsForMatId, setCountingSchoolsForMatId] = useState(null);
  // Identifies the one school count allowed to open a confirmation; anything else is stale.
  const currentArchiveCount = useRef(null);
  const isCountingSchools = countingSchoolsForMatId !== null;

  // Derive all school IDs for a MAT group row, excluding archived schools
  const getGroupSchoolIds = (row) => {
    // Loaded group details hold only the active schools
    const groupDetails = loadedGroupDetails[row.Id];
    if (groupDetails?.Schools) return schoolIdsOf(groupDetails);

    // Fallback to MatSchoolProductsList (archive status not available at this level)
    const fromProducts = (row.MatSchoolProductsList || [])
      .map(p => p.SchoolId)
      .filter(id => !!id && id > 0);
    return Array.from(new Set(fromProducts));
  };

  // MAT list rows don't carry school archive status, so group details are loaded on demand
  // (expand, group impersonation, archive) and cached. Concurrent callers share one request.
  const loadGroupDetails = (matId) => {
    if (loadedGroupDetails[matId]) return Promise.resolve(loadedGroupDetails[matId]);
    if (groupDetailsInFlight.current[matId]) return groupDetailsInFlight.current[matId];

    setLoadingGroupDetails((prev) => ({ ...prev, [matId]: true }));
    const request = groupService
      .getMatGroupDetails(matId)
      .then((response) => {
        if (!response?.success || !response.data) return null;
        const details = withActiveSchools(response.data);
        setLoadedGroupDetails((prev) => ({ ...prev, [matId]: details }));
        return details;
      })
      .catch((error) => {
        console.error(`Error loading group details for ${matId}:`, error);
        return null;
      })
      .finally(() => {
        delete groupDetailsInFlight.current[matId];
        setLoadingGroupDetails((prev) => ({ ...prev, [matId]: false }));
      });
    groupDetailsInFlight.current[matId] = request;
    return request;
  };

  const getNonArchivedSchoolIdsForMat = async (matId) => schoolIdsOf(await loadGroupDetails(matId));

  // Load MAT groups - using paginated API with server-side filtering, sorting, and pagination
  const loadMatGroups = React.useCallback(() => {
    setLoading(true);

    // Convert page (0-based) to pageNumber (1-based) for API
    const pageNumber = page + 1;
    const includeOnlyTestAccounts = value === 1; // Tab 0 = Live Groups, Tab 1 = Test Groups

    groupService
      .getAllMatGroupsPaginated(pageNumber, rowsPerPage, orderBy, order, debouncedSearchQuery || '', includeOnlyTestAccounts)
      .then((response) => {
        if (response.success && response.data) {
          const paginatedData = Array.isArray(response.data) ? response.data : [];

          setAdminDashboardInfo({ data: paginatedData });
          setTotalCount(response.total || 0);
          setLoading(false);
        } else {
          setLoading(false);
          setError('Failed to load groups');
        }
      })
      .catch((err) => {
        console.error('Error loading MAT groups:', err);
        setLoading(false);
        setError('Error loading groups');
      });
  }, [page, rowsPerPage, orderBy, order, debouncedSearchQuery, value]);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page to 0 when search, sort, or filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery, orderBy, order, value]);

  // A page, tab, sort or search change replaces the rows on screen, so a school count still in
  // flight belongs to a MAT the user may no longer see: drop it rather than open its confirmation.
  useEffect(() => {
    currentArchiveCount.current = null;
    setCountingSchoolsForMatId(null);
    loadMatGroups();
  }, [loadMatGroups]);

  useEffect(() => () => {
    currentArchiveCount.current = null;
  }, []);

  const handleSchoolDetails = (id) => {
    navigate.push(`/school-detail?schoolId=${id}`);
  };

  const handleMatDetails = (id) => {
    navigate.push(`/group-detail?matId=${id}`);
  };


  // Identity stores no school or group records, so the audit trail can only name the customer a
  // super admin entered if we send the name with the impersonation. Both are already on screen
  // here; when one can't be resolved the API falls back to "<Type> <Id>".
  const auditEntityName = {
    group: (matId) =>
      (adminDashboardInfo.data || []).find((m) => String(m?.Id) === String(matId))?.Name || '',
    school: (matId, schoolId) =>
      (loadedGroupDetails[matId]?.Schools || []).find(
        (s) => String(s?.Id) === String(schoolId)
      )?.Name || '',
  };

  const handleEDUIntellMATclick = async (MATId, _SchoolId, _groupSchoolIds) => {
    const callbackUrl = getCallbackUrlForProduct(Product.EDU_INTELLIGENCE);
    const buttonKey = `edu-mat-${MATId}`;

    const nonArchivedSchoolIds = await getNonArchivedSchoolIdsForMat(MATId);

    await impersonate({
      entityType: ScopeType.GROUP,
      entityId: MATId,
      entityName: auditEntityName.group(MATId),
      schoolIds: nonArchivedSchoolIds,      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[Product.EDU_INTELLIGENCE],
      productType: Product.EDU_INTELLIGENCE,
    });
  };
  const handleEDUIntellSchoolclick = async (MATId, SchoolId) => {
    const callbackUrl = getCallbackUrlForProduct(Product.EDU_INTELLIGENCE);
    const buttonKey = `edu-school-${SchoolId}`;

    await impersonate({
      entityType: ScopeType.SCHOOL,
      entityId: SchoolId,
      entityName: auditEntityName.school(MATId, SchoolId),
      groupId: MATId,
      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[Product.EDU_INTELLIGENCE],
      productType: Product.EDU_INTELLIGENCE,
    });
  };

  const handleSchoolInsightsclick = async (MATId, SchoolId, productId) => {
    const callbackUrl = getCallbackUrlForProduct(productId);
    const buttonKey = `insights-school-${productId}-${SchoolId}`;

    await impersonate({
      entityType: ScopeType.SCHOOL,
      entityId: SchoolId,
      entityName: auditEntityName.school(MATId, SchoolId),
      groupId: MATId,
      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[productId] || 'Insights',
      productType: productId,
    });
  };

  const handleMatInsightsclick = async (MATId, _SchoolId, productId, _groupSchoolIds) => {
    const callbackUrl = getCallbackUrlForProduct(productId);
    const buttonKey = `insights-mat-${productId}-${MATId}`;

    const nonArchivedSchoolIds = await getNonArchivedSchoolIdsForMat(MATId);

    await impersonate({
      entityType: ScopeType.GROUP,
      entityId: MATId,
      entityName: auditEntityName.group(MATId),
      schoolIds: nonArchivedSchoolIds,      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[productId] || 'Insights',
      productType: productId,
    });
  };

  const handleSchoolWelbeingclick = async (MATId, SchoolId, email) => {
    const callbackUrl = getCallbackUrlForProduct(Product.MAIN_APP_TOOLKIT);
    const buttonKey = `wellbeing-school-${SchoolId}`;

    await impersonate({
      entityType: ScopeType.SCHOOL,
      entityId: SchoolId,
      entityName: auditEntityName.school(MATId, SchoolId),
      groupId: MATId,
      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[Product.MAIN_APP_TOOLKIT],
      productType: Product.MAIN_APP_TOOLKIT,
      email,
    });
  };

  const handleMatWelbeingclick = async (MATId, _SchoolId, _MATToken, _groupSchoolIds, email) => {
    const callbackUrl = getCallbackUrlForProduct(Product.MAIN_APP_TOOLKIT);
    const buttonKey = `wellbeing-mat-${MATId}`;

    const nonArchivedSchoolIds = await getNonArchivedSchoolIdsForMat(MATId);

    await impersonate({
      entityType: ScopeType.GROUP,
      entityId: MATId,
      entityName: auditEntityName.group(MATId),
      schoolIds: nonArchivedSchoolIds,      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: PRODUCT_DISPLAY_NAMES[Product.MAIN_APP_TOOLKIT],
      productType: Product.MAIN_APP_TOOLKIT,
      email,
    });
  };

  const handleSchoolHomeclick = async (MATId, SchoolId) => {
    const callbackUrl = getHomeCallbackUrl(ScopeType.SCHOOL);
    const buttonKey = `home-school-${SchoolId}`;

    await impersonate({
      entityType: ScopeType.SCHOOL,
      entityId: SchoolId,
      entityName: auditEntityName.school(MATId, SchoolId),
      groupId: MATId,
      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: 'Home',
    });
  };

  const handleMatHomeclick = async (MATId, _SchoolId, _groupSchoolIds) => {
    const callbackUrl = getHomeCallbackUrl(ScopeType.GROUP);
    const buttonKey = `home-mat-${MATId}`;

    const nonArchivedSchoolIds = await getNonArchivedSchoolIdsForMat(MATId);

    await impersonate({
      entityType: ScopeType.GROUP,
      entityId: MATId,
      entityName: auditEntityName.group(MATId),
      schoolIds: nonArchivedSchoolIds,      callbackUrl,
      setLoading: (loading) => {
        setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
      },
      productName: 'Home',
    });
  };

  // Null when the count can't be known, so the confirmation stays truthful rather than
  // claiming the MAT has no schools.
  const countActiveSchools = async (matId) => {
    const details = await loadGroupDetails(matId);
    return details ? activeSchoolCountOf(details) : null;
  };

  const forgetGroupDetails = (matId) => {
    setExpandedRows((rows) => rows.filter((rowId) => rowId !== matId));
    setLoadedGroupDetails(({ [matId]: _dropped, ...rest }) => rest);
  };

  // Archiving the last row on a later page would leave the pager past the end; step back a
  // page instead (which reloads through the pagination effect).
  const reloadAfterGroupLeavesList = () => {
    if (groupRows.length === 1 && page > 0) {
      setPage(page - 1);
      return;
    }
    loadMatGroups();
  };

  // One archive at a time: a click while another MAT's count is loading is ignored, and a count
  // that has been superseded never opens (or replaces) the confirmation.
  const handleArchiveMatClick = async (row) => {
    if (currentArchiveCount.current) return;
    const countRequest = { matId: row.Id };
    currentArchiveCount.current = countRequest;
    setCountingSchoolsForMatId(row.Id);

    const activeSchoolCount = await countActiveSchools(row.Id);
    if (currentArchiveCount.current !== countRequest) return;
    currentArchiveCount.current = null;
    setCountingSchoolsForMatId(null);

    requestConfirmation(
      archiveMatAction({ matId: row.Id, matName: row.Name, activeSchoolCount }),
      () => {
        forgetGroupDetails(row.Id);
        reloadAfterGroupLeavesList();
      }
    );
  };

  // The expanded panel lists only active schools, so an archived one simply drops out of it.
  const handleArchiveSchoolClick = (matId, school) => {
    if (currentArchiveCount.current) return;
    requestConfirmation(archiveSchoolAction({ schoolId: school.Id }), () => {
      setLoadedGroupDetails((prev) => {
        const mat = prev[matId];
        if (!mat) return prev;
        return { ...prev, [matId]: { ...mat, Schools: mat.Schools.filter((s) => s.Id !== school.Id) } };
      });
    });
  };

  const handleWelbeeChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleWelbeeChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleWelbeeSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleExpand = (Id) => {
    if (expandedRows.includes(Id)) {
      setExpandedRows(expandedRows.filter((rowId) => rowId !== Id));
      return;
    }
    setExpandedRows([...expandedRows, Id]);
    loadGroupDetails(Id);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value); // Update search query
    setPage(0); // Reset to the first page when searching
    // loadMatGroups will be triggered by useEffect when searchQuery changes
  };

  // Use data directly from API (server-side filtered, sorted, and paginated)
  const groupRows = adminDashboardInfo.data || [];

  // A sortable purple-header cell: label + a direction glyph reflecting the active column.
  const sortHeader = (property, label) => {
    const active = orderBy === property;
    const Glyph = active ? (order === 'desc' ? ArrowDownwardIcon : ArrowUpwardIcon) : SwapVertIcon;
    return (
      <SortHead type='button' $active={active} onClick={() => handleWelbeeSort(property)}>
        {label}
        <Glyph />
      </SortHead>
    );
  };

  // Group-level product chips (group/MAT impersonation), keeping the existing handlers/keys.
  const renderGroupProducts = (row) => (
    <Stack direction='row' flexWrap='wrap' rowGap={0.5} columnGap={0.5}>
      {row.MatSchoolProductsList && (row.MatSchoolProductsList || []).length > 0 ? (
        row.MatSchoolProductsList
          .filter((item, index, self) => self.findIndex((i) => i.Product === item.Product) === index)
          .map((marProducts) => (
            <React.Fragment key={marProducts.id}>
              {marProducts.Product === Product.MAIN_APP_TOOLKIT && (
                <ProdButton
                  label='Main App & Toolkit'
                  onClick={() => handleMatWelbeingclick(
                    marProducts.MatId,
                    marProducts.SchoolId,
                    row.GroupDirectors?.[0]?.UserId ?? null,
                    getGroupSchoolIds(row),
                    row.Email
                  )}
                  className='staff-chip'
                  size='small'
                  disabled={impersonateLoading[`wellbeing-mat-${marProducts.MatId}`]}
                >
                  {impersonateLoading[`wellbeing-mat-${marProducts.MatId}`] ? 'Loading...' : 'Main App & Toolkit'}
                </ProdButton>
              )}
              {marProducts.Product === Product.STUDENT_INSIGHT && (
                <ProdButton
                  label='Student Insight'
                  onClick={() => handleMatInsightsclick(
                    marProducts.MatId,
                    marProducts.SchoolId,
                    Product.STUDENT_INSIGHT,
                    getGroupSchoolIds(row)
                  )}
                  className='parent-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-mat-${Product.STUDENT_INSIGHT}-${marProducts.MatId}`]}
                >
                  {impersonateLoading[`insights-mat-${Product.STUDENT_INSIGHT}-${marProducts.MatId}`] ? 'Loading...' : 'Student Insight'}
                </ProdButton>
              )}
              {marProducts.Product === Product.PARENTS_INSIGHTS && (
                <ProdButton
                  label='Parents Insights'
                  onClick={() => handleMatInsightsclick(
                    marProducts.MatId,
                    marProducts.SchoolId,
                    Product.PARENTS_INSIGHTS,
                    getGroupSchoolIds(row)
                  )}
                  className='insight-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-mat-${Product.PARENTS_INSIGHTS}-${marProducts.MatId}`]}
                >
                  {impersonateLoading[`insights-mat-${Product.PARENTS_INSIGHTS}-${marProducts.MatId}`] ? 'Loading...' : 'Parents Insights'}
                </ProdButton>
              )}
              {marProducts.Product === Product.STAFF_INSIGHTS && (
                <ProdButton
                  label='Staff Insights'
                  onClick={() => handleMatInsightsclick(
                    marProducts.MatId,
                    marProducts.SchoolId,
                    Product.STAFF_INSIGHTS,
                    getGroupSchoolIds(row)
                  )}
                  className='student-chip'
                  size='small'
                  disabled={impersonateLoading[`insights-mat-${Product.STAFF_INSIGHTS}-${marProducts.MatId}`]}
                >
                  {impersonateLoading[`insights-mat-${Product.STAFF_INSIGHTS}-${marProducts.MatId}`] ? 'Loading...' : 'Staff Insights'}
                </ProdButton>
              )}
              {EI_TIER_PRODUCT_IDS.includes(marProducts.Product) && (
                <ProdButton
                  label={PRODUCT_DISPLAY_NAMES[marProducts.Product]}
                  onClick={() => handleEDUIntellMATclick(
                    marProducts.MatId,
                    marProducts.SchoolId,
                    getGroupSchoolIds(row)
                  )}
                  className='edu-chip'
                  size='small'
                  disabled={impersonateLoading[`edu-mat-${marProducts.MatId}`]}
                >
                  {impersonateLoading[`edu-mat-${marProducts.MatId}`] ? 'Loading...' : PRODUCT_DISPLAY_NAMES[marProducts.Product]}
                </ProdButton>
              )}
              {marProducts.Product === Product.PULSE && (
                <Chip label='Pulse' className='lparent-chip' size='small' />
              )}
            </React.Fragment>
          ))
      ) : (
        <Chip label='No products available.' className='learning-chip' size='small' />
      )}
    </Stack>
  );

  const renderGroupActions = (row) => (
    <Stack direction='row' spacing={0.5}>
      <HomeButton
        type='button'
        onClick={() => handleMatHomeclick(row.Id, 0, getGroupSchoolIds(row))}
        disabled={impersonateLoading[`home-mat-${row.Id}`]}
      >
        {impersonateLoading[`home-mat-${row.Id}`] ? 'Loading...' : 'Home'}
      </HomeButton>
      <PillButton type='button' onClick={() => handleMatDetails(row.Id)}>Detail</PillButton>
      {canArchive && (
        <PillButton
          type='button'
          $variant='danger'
          onClick={() => handleArchiveMatClick(row)}
          disabled={isCountingSchools}
          aria-label={`Archive ${row.Name}`}
          aria-busy={countingSchoolsForMatId === row.Id}
        >
          {countingSchoolsForMatId === row.Id ? 'Loading...' : 'Archive'}
        </PillButton>
      )}
    </Stack>
  );

  // Per-school product chips (school-level impersonation), keeping the existing handlers/keys.
  const renderSchoolProducts = (row, detail) => {
    const schoolEmail =
      detail?.HeadTeachers?.[0]?.Email ||
      detail?.Email ||
      row.Email ||
      '';
    return (
      <Stack direction='row' flexWrap='wrap' rowGap={0.5} columnGap={0.5}>
        {detail.SchoolProducts && detail.SchoolProducts.length > 0 ? (
          detail.SchoolProducts
            .filter((item, index, self) => self.findIndex((i) => i.Product === item.Product) === index)
            .map((schoolProds) => (
              <React.Fragment key={schoolProds.id}>
                {schoolProds.Product === Product.MAIN_APP_TOOLKIT && (
                  <ProdButton
                    label='Main App & Toolkit'
                    onClick={() => handleSchoolWelbeingclick(
                      schoolProds.MatId,
                      schoolProds.SchoolId,
                      schoolEmail
                    )}
                    className='staff-chip'                    
                    disabled={impersonateLoading[`wellbeing-school-${schoolProds.SchoolId}`]}
                  >
                    {impersonateLoading[`wellbeing-school-${schoolProds.SchoolId}`] ? 'Loading...' : 'Main App & Toolkit'}
                  </ProdButton>
                )}
                {schoolProds.Product === Product.STUDENT_INSIGHT && (
                  <ProdButton
                    label='Student Insight'
                    onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.STUDENT_INSIGHT)}
                    className='parent-chip'                    
                    disabled={impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${schoolProds.SchoolId}`]}
                  >
                    {impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Student Insight'}
                  </ProdButton>
                )}
                {schoolProds.Product === Product.PARENTS_INSIGHTS && (
                  <ProdButton
                    label='Parents Insights'
                    onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.PARENTS_INSIGHTS)}
                    className='insight-chip'                    
                    disabled={impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${schoolProds.SchoolId}`]}
                  >
                    {impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Parents Insights'}
                  </ProdButton>
                )}
                {schoolProds.Product === Product.STAFF_INSIGHTS && (
                  <ProdButton
                    label='Staff Insights'
                    onClick={() => handleSchoolInsightsclick(schoolProds.MatId, schoolProds.SchoolId, Product.STAFF_INSIGHTS)}
                    className='student-chip'                    
                    disabled={impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${schoolProds.SchoolId}`]}
                  >
                    {impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${schoolProds.SchoolId}`] ? 'Loading...' : 'Staff Insights'}
                  </ProdButton>
                )}
                {EI_TIER_PRODUCT_IDS.includes(schoolProds.Product) && (
                  <ProdButton
                    label={PRODUCT_DISPLAY_NAMES[schoolProds.Product]}
                    onClick={() => handleEDUIntellSchoolclick(schoolProds.MatId, schoolProds.SchoolId)}
                    className='edu-chip'                    
                    disabled={impersonateLoading[`edu-school-${schoolProds.SchoolId}`]}
                  >
                    {impersonateLoading[`edu-school-${schoolProds.SchoolId}`] ? 'Loading...' : PRODUCT_DISPLAY_NAMES[schoolProds.Product]}
                  </ProdButton>
                )}
                {schoolProds.Product === Product.PULSE && (
                  <ProdButton label='Pulse' className='lparent-chip'>Pulse</ProdButton>
                )}
              </React.Fragment>
            ))
        ) : (
          <Chip label='No products available.' className='learning-chip' size='small' />
        )}
      </Stack>
    );
  };

  const renderSchoolActions = (row, detail) => (
    <Stack direction='row' spacing={0.5} >
      <HomeButton
        type='button'
        onClick={() => handleSchoolHomeclick(row.Id, detail.Id)}
        disabled={impersonateLoading[`home-school-${detail.Id}`]}
      >
        {impersonateLoading[`home-school-${detail.Id}`] ? 'Loading...' : 'Home'}
      </HomeButton>
      <PillButton type='button' onClick={() => handleSchoolDetails(detail.Id)}>Detail</PillButton>
      {canArchive && (
        <PillButton
          type='button'
          $variant='danger'
          onClick={() => handleArchiveSchoolClick(row.Id, detail)}
          disabled={isCountingSchools}
          aria-label={`Archive ${detail.Name}`}
        >
          Archive
        </PillButton>
      )}
    </Stack>
  );

  const buildSchoolColumns = (row) => [
    { key: 'name', label: 'School Name', width: '20%', render: (d) => <CellText>{d.Name}</CellText> },
    { key: 'urn', label: 'School URN', width: '10%', render: (d) => d.WondeeSchoolUrn },
    { key: 'product', label: 'Products', width: '50%', render: (d) => renderSchoolProducts(row, d) },
    { key: 'action', label: 'Action', width: '20%', render: (d) => renderSchoolActions(row, d) },
  ];

  const groupColumns = [
    {
      key: 'expand',
      label: '',
      width: '4%',
      align: 'center',
      render: (row) => (
        <ExpandToggle onClick={() => handleExpand(row.Id)} disableRipple aria-label='Toggle schools'>
          {expandedRows.includes(row.Id) ? <ExpandLess /> : <ExpandMore />}
        </ExpandToggle>
      ),
    },
    { key: 'name', header: sortHeader('Name', 'Group Name'), width: '16%', render: (row) => <CellText>{row.Name}</CellText> },
    { key: 'welbeeId', header: sortHeader('Id', 'GroupWelbeeId'), width: '10%', render: (row) => row.Id },
    { key: 'uid', header: sortHeader('MatGroupUID', 'GroupUID'), width: '10%', render: (row) => row.MatGroupUID || row.GroupUID || '' },
    { key: 'holder', header: sortHeader('Email', 'Account Holder'), width: '15%', render: (row) => row.Email },
    { key: 'products', header: sortHeader('Products', 'Products'), width: '25%', render: (row) => renderGroupProducts(row) },
    { key: 'action', label: 'Action', width: '20%', render: (row) => renderGroupActions(row) },
  ];

  const renderExpanded = (row) => {
    const isLoadingDetails = loadingGroupDetails[row.Id];
    const schools = loadedGroupDetails[row.Id]?.Schools || [];
    return (
      <SchoolPanel>
        <SchoolPanelTitle>Schools</SchoolPanelTitle>
        <DataTable         
          columns={buildSchoolColumns(row)}
          rows={isLoadingDetails ? [] : schools}
          getRowKey={(d) => d.Id}
          empty={
            isLoadingDetails ? (
              <CenterState $pad='16px 0'>
                <CircularProgress size={20} sx={{ color: sa.color.teal }} />
              </CenterState>
            ) : (
              'No schools found for this group.'
            )
          }                
        />
      </SchoolPanel>
    );
  };

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <SectionHeaderRow title='Groups' size='lg'>
            <SearchBox>
              <SearchIcon />
              <input
                type='text'
                placeholder='Enter keyword'
                value={searchQuery}
                onChange={handleSearch}
                aria-label='Search groups'
              />
            </SearchBox>
            <PillButton type='button' $variant='danger' $size='lg' onClick={() => navigate.push('/register-group')}>
              Create Group
            </PillButton>
          </SectionHeaderRow>

          <div style={{ marginTop: sa.space.section }}>
            <TabBar role='tablist' aria-label='group listing tabs'>
              <TabPill type='button' role='tab' $active={value === 0} aria-selected={value === 0} onClick={() => handleTabChange(0)}>
                Live Groups
              </TabPill>
              <TabPill type='button' role='tab' $active={value === 1} aria-selected={value === 1} onClick={() => handleTabChange(1)}>
                Test Groups
              </TabPill>
            </TabBar>
          </div>

          <div style={{ marginTop: sa.space.section }}>
           
              {loading ? (
                <CenterState>
                  <CircularProgress sx={{ color: sa.color.teal }} />
                </CenterState>
              ) : error ? (
                <CenterState $error>{error}</CenterState>
              ) : (
                <>
                  <DataTable
                    columns={groupColumns}
                    rows={groupRows}
                    getRowKey={(row) => row.Id}
                    empty='No groups found.'
                    isRowExpanded={(row) => expandedRows.includes(row.Id)}
                    renderExpanded={renderExpanded}
                    //pageSize={15}
                    //size='sm'
                  />
                  <PaginationWrap>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 30, 50]}
                      component='div'
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleWelbeeChangePage}
                      onRowsPerPageChange={handleWelbeeChangeRowsPerPage}
                    />
                  </PaginationWrap>
                </>
              )}
            
          </div>
          <ConfirmDialog {...archiveDialogProps} />
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

export default withRouter(GroupListing);
